import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, KeyRound, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/contexts/ToastContext'
import { isSuperAdmin } from '@/lib/roles'
import { superadminForceResetPassword } from '@/lib/authAdminApi'
import { useTranslation } from 'react-i18next'

export const SuperAdminForcePasswordPage: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { success, error: showError } = useToast()
  const [targetEmail, setTargetEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (user && !isSuperAdmin(user)) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, navigate])

  if (user && !isSuperAdmin(user)) {
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const te = targetEmail.trim().toLowerCase()
    if (!te) {
      showError('Enter the user email')
      return
    }
    if (te === user?.email?.toLowerCase().trim()) {
      showError('Use Settings → change password for your own account')
      return
    }
    if (newPassword !== confirmPassword) {
      showError(t('settingsPage.passwordsMismatch'))
      return
    }
    if (newPassword.length < 8) {
      showError(t('settingsPage.passwordMin'))
      return
    }
    if (!/[a-zA-Z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      showError('Password must include at least one letter and one number')
      return
    }

    setSubmitting(true)
    try {
      await superadminForceResetPassword(te, newPassword)
      success('Password updated', `${te} must sign in again on all devices.`)
      setTargetEmail('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : 'Failed to reset password')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-4 sm:px-6 py-4 flex items-center gap-4 max-w-2xl mx-auto">
          <Button variant="secondary" size="sm" type="button" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-amber-600" />
              {t('dashboardPage.forcePasswordReset')}
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Super admin only. Updates auth.users and signs the user out everywhere.
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-8 max-w-md mx-auto">
        <Card className="border-amber-200 dark:border-amber-900/50">
          <CardHeader>
            <CardTitle className="text-base">{t('dashboardPage.forcePasswordReset')}</CardTitle>
            <CardDescription>
              Only internal accounts: addresses in the admin allow-list or <code className="text-xs">@sea-net.co.kr</code>. Same password rules as signup (8+ chars, letter and number).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('fields.email')}</label>
                <Input
                  type="email"
                  autoComplete="off"
                  value={targetEmail}
                  onChange={(e) => setTargetEmail(e.target.value)}
                  placeholder="admin@example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('settingsPage.newPassword')}</label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('settingsPage.confirmNewPassword')}</label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('settingsPage.updatingPassword')}
                  </>
                ) : (
                  'Update password in auth'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
